import Big from 'big.js';
import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import * as models from 'smg/customer-api/models';
import { OrderAggregates, Orders } from 'smg/customer-api/orders';
import { QuoteRequestProjects } from 'smg/customer-api/quoterequestprojects';
import * as session from 'smg/customer-api/session';
import { MultiLoader } from 'smg/multiloader';
import { Picture } from 'smg/picture';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import * as carts from '../carts';
import { Relref } from '../hugo';
import { OrganizationView, RenderOrganizationSwitch, ResolveOrganizationView } from '../organization-view';
import * as payments from '../payments';
import { ProjectStatusLabel, ProjectStatusPillClasses, ProjectOrdersLine } from '../projects';
import { TimelineLoaders } from '../timeline';

void main();

function formatMed(dt: DateTime): string {
    return dt.toLocaleString(DateTime.DATE_MED);
}

async function main() {
    if (await AccountReady) {
        return;
    }

    const $loader = document.querySelector('#dashboard-loader') as HTMLElement;
    const $dashboardContent = document.querySelector('#dashboard-content') as HTMLElement;
    const $attentionSection = document.querySelector('#attention-section') as HTMLElement;
    const $attentionCount = document.querySelector('#attention-count') as HTMLElement;
    const $attentionList = document.querySelector('#attention-list') as HTMLElement;
    const $allCaughtUp = document.querySelector('#all-caught-up') as HTMLElement;
    const $projectsSection = document.querySelector('#projects-section') as HTMLElement;
    const $projectsCount = document.querySelector('#projects-count') as HTMLElement;
    const $projectsList = document.querySelector('#projects-list') as HTMLElement;
    const $noProjects = document.querySelector('#no-projects') as HTMLElement;
    const $dashboardError = document.querySelector('#dashboard-error') as HTMLElement;
    const $dashboardErrorRetry = document.querySelector('#dashboard-error-retry') as HTMLElement;
    const $firstTimer = document.querySelector('#first-timer') as HTMLElement;
    const $unpaid = document.querySelector('#unpaid') as HTMLElement;
    const $unpaidTotal = document.querySelector('#unpaid-total') as HTMLElement;
    const $unpaidCount = document.querySelector('#unpaid-count') as HTMLElement;
    const $unpaidPay = document.querySelector('#unpaid-pay') as HTMLElement;
    const $statsCard = document.querySelector('#stats-card') as HTMLElement;
    const $accountBalance = document.querySelector('#account-balance') as HTMLElement;
    const $accountBalanceAmount = document.querySelector('#account-balance-amount') as HTMLElement;
    const $availableDiscount = document.querySelector('#available-discount') as HTMLElement;
    const $availableDiscountAmount = document.querySelector('#available-discount-amount') as HTMLElement;
    const $orderHistory = document.querySelector('#order-history') as HTMLElement;
    const $orderHistoryCount = document.querySelector('#order-history-count') as HTMLElement;
    const $orderHistoryTotal = document.querySelector('#order-history-total') as HTMLElement;
    const $recentActivitySection = document.querySelector('#recent-activity-section') as HTMLElement;
    const $recentActivityItems = document.querySelector('#recent-activity-items') as HTMLElement;
    const $recentActivityFooter = document.querySelector('#recent-activity-footer') as HTMLElement;
    const $recentActivityOlder = document.querySelector('#recent-activity-older') as HTMLElement;

    $dashboardErrorRetry.addEventListener('click', () => {
        window.location.reload();
    });

    const sessP = session.Session();
    const userP = sessP.then(sess => sess.user);

    // Both the session and the org roster are resolved inside one try: a
    // non-403 session read failure is swallowed by session.Redirect() above (it
    // only redirects on 403), so awaiting userP can still reject — without the
    // guard that rejection would leave the spinner up forever with no card.
    let user: models.User;
    let view: OrganizationView;
    try {
        user = await userP;
        view = await ResolveOrganizationView(user);
    } catch {
        $loader.remove();
        $dashboardError.classList.remove('d-none');
        $dashboardContent.classList.remove('d-none');
        return;
    }
    RenderOrganizationSwitch(view, user);

    const unpaidQuery = {
        q: 'balance:>=.01',
        data: 'balance,invoiceNumber,userID',
        max: 9999,
    };
    const organizationParams = view.params();

    let projects: models.QuoteRequestProjectsHyejooProject[] = [];
    let projectsFailed = false;
    let unpaidOrders: models.HyejooOrder[] = [];
    let unpaidLoaded = false;
    let unpaidIDs: string[] = [];
    let unpaidTotal = Big(0);
    let orderCount = 0;
    let orderCountLoaded = false;
    let orderTotal = Big(0);
    let hasPositiveBalance = false;
    let accountBalance = Big(0);
    let hasAvailableDiscount = false;
    let availableDiscount = Big(0);

    const projectsP = QuoteRequestProjects({
        max: 50,
        data: 'name,updated,balanceDue,userID,status,waitingOn,quote,orders',
    }, view.params()).then(data => {
        projects = data.results;
    }).catch(() => {
        projectsFailed = true;
        $dashboardError.classList.remove('d-none');
    });

    const ordersStatsP = OrderAggregates('', ['id', 'total'], organizationParams).then(resp => {
        orderCount = resp.aggregates.get('id')?.count ?? 0;
        orderCountLoaded = true;
        orderTotal = Big(resp.aggregates.get('total')?.sum ?? 0);
    }).catch(() => undefined);

    const unpaidP = Orders(unpaidQuery, organizationParams).then(data => {
        unpaidOrders = data.results;
        unpaidLoaded = true;
    }).catch(() => undefined);

    const discountP = session.SessionWith(['availableDiscount']);
    const balanceP = Promise.all([sessP, discountP]).then(([sess, discount]) => {
        // Account balance and available discount are both the viewer's own —
        // there is no org balance and no org discount. Under the lens they
        // would sit among the org-wide totals reading as the organization's
        // money, so they show only in the personal view.
        if (view.active) {
            return;
        }
        if (sess.user.balance?.gt('0')) {
            hasPositiveBalance = true;
            accountBalance = sess.user.balance;
        }
        // availableDiscount is a data= field on the users read.
        if (discount.user.availableDiscount.gt('0')) {
            hasAvailableDiscount = true;
            availableDiscount = discount.user.availableDiscount;
        }
    }).catch(() => undefined);

    await Promise.allSettled([projectsP, ordersStatsP, unpaidP, balanceP]);

    $loader.remove();
    $dashboardContent.classList.remove('d-none');

    const firstTimer = !view.active &&
        !projectsFailed &&
        projects.length === 0 &&
        orderCountLoaded &&
        orderCount === 0 &&
        unpaidLoaded &&
        unpaidOrders.length === 0 &&
        !hasPositiveBalance &&
        !hasAvailableDiscount;

    if (firstTimer) {
        $firstTimer.classList.remove('d-none');
        return;
    }

    if (view.active) {
        const orgName = view.organization?.name ?? '';
        const $attentionHeading = document.querySelector('#attention-heading');
        if ($attentionHeading) {
            $attentionHeading.textContent = 'Needs attention';
        }
        const $projectsHeading = document.querySelector('#projects-heading');
        if ($projectsHeading) {
            $projectsHeading.textContent = 'Projects';
        }
        const $noProjectsHeading = document.querySelector('#no-projects-heading');
        if ($noProjectsHeading) {
            $noProjectsHeading.textContent = 'Projects';
        }
        const $allCaughtUpText = document.querySelector('#all-caught-up-text');
        if ($allCaughtUpText) {
            $allCaughtUpText.textContent = `All caught up — nothing in ${orgName} needs attention right now.`;
        }
        const $noProjectsTitle = document.querySelector('#no-projects-title');
        if ($noProjectsTitle) {
            $noProjectsTitle.textContent = `No projects in ${orgName} yet`;
        }
        const $noProjectsBody = document.querySelector('#no-projects-body');
        if ($noProjectsBody) {
            $noProjectsBody.textContent = `When someone in ${orgName} starts a project, it appears here.`;
        }
        document.querySelector('#no-projects-ctas')?.classList.add('d-none');
    }

    $recentActivitySection.classList.remove('d-none');
    const ml = new MultiLoader({
        $target: $recentActivityItems,
        pageSize: 15,
        loaders: TimelineLoaders({ view }),
        done: () => {
            // Every loader exhausted — the link would only re-request
            // empty pages, so drop the whole footer row.
            $recentActivityFooter.remove();
            if ($recentActivityItems.childElementCount === 0) {
                $recentActivitySection.classList.add('d-none');
            }
        },
    });
    // load(true) forces the first page even when the rail feed sits
    // below the fold, where a plain load() would no-op on its
    // bottom-visibility check. A loader erroring is surfaced by
    // MultiLoader itself (ShowError) and must not take the page down.
    void ml.load(true).catch(() => undefined);

    $recentActivityOlder.addEventListener('click', () => {
        void ml.load().catch(() => undefined);
    });

    function payRow(order: models.HyejooOrder): HTMLLIElement {
        const $row = template.Clone('#attention-pay-row-template').firstElementChild as HTMLLIElement;
        const $amount = $row.querySelector('.pay-amount') as HTMLElement;
        const $invoice = $row.querySelector('.pay-invoice') as HTMLAnchorElement;
        const $payButton = $row.querySelector('.pay-button') as HTMLButtonElement;

        $amount.textContent = `${DollarFormat(order.balance)} due`;
        $invoice.textContent = String(order.invoiceNumber);
        $invoice.href = view.withLens(`${Relref('/orders/track')}?id=${order.id ?? ''}`);

        if (view.active) {
            const $attribution = $row.querySelector('.organization-attribution') as HTMLElement;
            view.fillAttribution($attribution, order.userID);
            $attribution.classList.remove('d-none');
        }

        $payButton.addEventListener('click', async () => {
            await payments.PromptPayment(await userP, [order.id ?? ''], Big(order.balance || 0), false);
            const data = await Orders(unpaidQuery, organizationParams);
            renderUnpaidAndAttention(data.results);
        });

        return $row;
    }

    function proofRow(project: models.QuoteRequestProjectsHyejooProject, quote: models.Quote): HTMLLIElement {
        const $row = template.Clone('#attention-proof-row-template').firstElementChild as HTMLLIElement;
        const $thumb = $row.querySelector('.proof-thumb') as HTMLImageElement;
        const $nameLink = $row.querySelector('.project-name-link') as HTMLAnchorElement;
        const $subline = $row.querySelector('.proof-subline') as HTMLElement;
        const $reviewProof = $row.querySelector('.review-proof') as HTMLAnchorElement;
        const $orderButton = $row.querySelector('.order-button') as HTMLButtonElement;

        const latest = quote.quoteProofs[quote.quoteProofs.length - 1];
        const $thumbWrap = $thumb.parentElement as HTMLElement | null;
        // The customer projects endpoint blanks proof.file, so getURLs() can
        // come back empty — the server-built main URL is the fallback.
        // Picture() strips and re-adds the transform params either way.
        const main = latest?.proof?.getURLs().main || latest?.proof?.main || '';
        const thumbnail = latest?.proof?.thumbnail;
        if (!main && !thumbnail) {
            $thumbWrap?.remove();
        } else {
            if (main) {
                void Picture($thumb, main, { displayWidth: 42, alt: 'Latest proof' });
                const $picture = $thumb.parentElement;
                if ($picture instanceof HTMLPictureElement) {
                    $picture.classList.add('d-block', 'w-100', 'h-100');
                }
            } else if (thumbnail) {
                $thumb.src = thumbnail;
            }

            // Stacked-deck edges behind the thumb signal there's more than
            // one proof on the quote; the revisions page shows all of them.
            if ($thumbWrap && quote.quoteProofs.length >= 2) {
                $thumbWrap.classList.add(quote.quoteProofs.length > 2 ? 'proof-stack-3' : 'proof-stack-2');
            }
        }

        if (quote.quoteProofs.length > 1) {
            $reviewProof.textContent = 'Review Proofs';
        }

        $nameLink.textContent = project.name;
        $nameLink.title = project.name;
        $nameLink.href = view.withLens(`${Relref('/projects/details')}?id=${project.id ?? ''}`);

        if (view.active) {
            const $attribution = $row.querySelector('.organization-attribution') as HTMLElement;
            view.fillAttribution($attribution, project.userID);
            $attribution.classList.remove('d-none');
        }

        // The quote key opens the review as the owner; the server sends it only
        // with the viewer's own quote.
        if (!view.canManage(project.userID) || !quote.key) {
            $reviewProof.remove();
        }

        const sentDate = latest?.created?.isValid ? formatMed(latest.created) : '';
        const proofCount = quote.quoteProofs.length;
        const proofsLabel = proofCount > 1 ? `${proofCount} proofs` : 'Proof';
        // Under the lens a colleague's proof waits on its owner, not the
        // viewer — the review is theirs.
        let whoseReview = 'your';
        if (view.active && !view.isOwn(project.userID)) {
            whoseReview = `${view.attributionName(project.userID)}'s`;
        }
        $subline.textContent = `${proofsLabel} ready for ${whoseReview} review · sent ${sentDate} · ${quote.serial}`;

        $reviewProof.href = `${Relref('/revisions')}?id=${quote.key}`;

        $orderButton.addEventListener('click', async () => {
            buttons.Start($orderButton);
            const added = await carts.AddCartItem({ quoteID: quote.id ?? '' }, { ...view.cartOptions(), promptToCheckout: false }).
                finally(() => buttons.Stop($orderButton));
            // Navigating away on a failed add would hide the error toast and
            // land the customer on a cart missing the quote.
            if (added) {
                window.location.href = Relref('/cart');
            }
        });

        return $row;
    }

    function renderUnpaidAndAttention(orders: models.HyejooOrder[]) {
        unpaidIDs = [];
        unpaidTotal = Big(0);
        for (const order of orders) {
            unpaidIDs.push(order.id ?? '');
            unpaidTotal = unpaidTotal.add(order.balance || 0);
        }

        if (orders.length) {
            $unpaidTotal.textContent = DollarFormat(unpaidTotal);
            $unpaidCount.textContent = `${orders.length.toLocaleString()} ${orders.length === 1 ? 'order' : 'orders'} with a balance`;
            $unpaid.classList.remove('d-none');
        } else {
            $unpaid.classList.add('d-none');
        }

        if (projectsFailed) {
            return;
        }

        $attentionList.replaceChildren();

        for (const order of orders) {
            $attentionList.append(payRow(order));
        }

        let proofCount = 0;
        for (const project of projects) {
            if (project.waitingOn !== 'you') {
                continue;
            }
            const quote = project.quote;
            if (!quote) {
                continue;
            }
            $attentionList.append(proofRow(project, quote));
            proofCount++;
        }

        const attentionCount = orders.length + proofCount;
        $attentionCount.textContent = String(attentionCount);

        if (attentionCount > 0) {
            $attentionSection.classList.remove('d-none');
            $allCaughtUp.classList.add('d-none');
        } else {
            $attentionSection.classList.add('d-none');
            $allCaughtUp.classList.remove('d-none');
        }
    }

    $unpaidPay.addEventListener('click', async () => {
        await payments.PromptPayment(await userP, unpaidIDs, unpaidTotal, false);
        const data = await Orders(unpaidQuery, organizationParams);
        renderUnpaidAndAttention(data.results);
    });

    renderUnpaidAndAttention(unpaidOrders);

    if (!projectsFailed && projects.length > 0) {
        $projectsCount.textContent = String(projects.length);
        for (const project of projects) {
            const $row = template.Clone('#project-row-template').firstElementChild as HTMLAnchorElement;
            const $name = $row.querySelector('.project-name') as HTMLElement;
            const $meta = $row.querySelector('.project-meta') as HTMLElement;
            const $status = $row.querySelector('.project-status') as HTMLElement;
            const $thumbWrap = $row.querySelector('.project-thumb-wrap') as HTMLElement;
            const $thumb = $row.querySelector('.project-thumb') as HTMLImageElement;

            // The endpoint's proofs are the latest-sent quote's set, ascending
            // by number and pre-hydrated — the last one is the highest sheet.
            // Same stacked-deck treatment as the attention rows above.
            const projectProofs = project.quote?.quoteProofs ?? [];
            const latestProof = projectProofs[projectProofs.length - 1];
            // proof.file is blanked by the endpoint — see proofRow above.
            const main = latestProof?.proof?.getURLs().main || latestProof?.proof?.main || '';
            const thumbnail = latestProof?.proof?.thumbnail;
            if (main || thumbnail) {
                if (main) {
                    void Picture($thumb, main, { displayWidth: 42, alt: 'Latest proof' });
                    const $picture = $thumb.parentElement;
                    if ($picture instanceof HTMLPictureElement) {
                        $picture.classList.add('d-block', 'w-100', 'h-100');
                    }
                } else if (thumbnail) {
                    $thumb.src = thumbnail;
                }
                if (projectProofs.length >= 2) {
                    $thumbWrap.classList.add(projectProofs.length > 2 ? 'proof-stack-3' : 'proof-stack-2');
                }
                $thumbWrap.classList.remove('d-none');
            }

            $row.href = view.withLens(`${Relref('/projects/details')}?id=${project.id ?? ''}`);
            $name.textContent = project.name;
            $name.title = project.name;

            if (view.active) {
                const $attribution = $row.querySelector('.organization-attribution') as HTMLElement;
                view.fillAttribution($attribution, project.userID);
                $attribution.classList.remove('d-none');
            }

            let balanceSuffix = '';
            if (project.balanceDue.gt(0)) {
                balanceSuffix = ` · ${DollarFormat(project.balanceDue)} due`;
            } else if (project.balanceDue.lt(0)) {
                balanceSuffix = ` · ${DollarFormat(project.balanceDue.abs())} credit`;
            }
            let proofsPrefix = '';
            if (projectProofs.length === 1) {
                proofsPrefix = '1 proof · ';
            } else if (projectProofs.length > 1) {
                proofsPrefix = `${projectProofs.length} proofs · `;
            }
            $meta.textContent = `${proofsPrefix}${ProjectOrdersLine(project.orders)} · updated ${formatMed(project.updated)}${balanceSuffix}`;

            $status.textContent = ProjectStatusLabel(project.status);
            $status.classList.add(...ProjectStatusPillClasses(project.status));

            $projectsList.append($row);
        }
        $projectsSection.classList.remove('d-none');
    } else if (!projectsFailed && (view.active || orderCount > 0)) {
        $noProjects.classList.remove('d-none');
    }

    let showBalance = false;
    if (hasPositiveBalance) {
        $accountBalanceAmount.textContent = DollarFormat(accountBalance);
        $accountBalance.classList.remove('d-none');
        showBalance = true;
    }

    let showDiscount = false;
    if (hasAvailableDiscount) {
        $availableDiscountAmount.textContent = DollarFormat(availableDiscount);
        $availableDiscount.classList.remove('d-none');
        showDiscount = true;
    }

    // The two figures checkout spends on its own share one card so a customer
    // holding both reads them as siblings; the rule only exists between them.
    if (showBalance && showDiscount) {
        $availableDiscount.classList.add('border-top', 'pt-3', 'mt-3');
    }
    if (showBalance || showDiscount) {
        $statsCard.classList.remove('d-none');
    }

    if (orderCountLoaded && orderCount > 0) {
        $orderHistoryCount.textContent = orderCount.toLocaleString();
        $orderHistoryTotal.textContent = DollarFormat(orderTotal);
        $orderHistory.classList.remove('d-none');
    }
}
